import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Upload,
  FileText,
  CheckCircle,
  ArrowRight,
  X,
  Briefcase,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TaxIntake() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [files, setFiles] = useState([]);
  
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    address: "",
    filing_status: "",
    has_dependents: false,
    dependents_count: 0,
    employment_type: "",
    is_business_owner: false,
    filed_last_year: false,
    notes: "",
    honeypot: "",
  });

  const filingStatuses = [
    { value: "single", label: "Single" },
    { value: "married_jointly", label: "Married Filing Jointly" },
    { value: "married_separately", label: "Married Filing Separately" },
    { value: "head_of_household", label: "Head of Household" },
  ];

  const employmentTypes = [
    { value: "w2", label: "W-2 Employee" },
    { value: "self_employed", label: "Self-Employed / 1099" },
    { value: "both", label: "Both W-2 and Self-Employed" },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check
    if (formData.honeypot) return;

    // Validation
    if (!formData.full_name || !formData.email || !formData.phone || 
        !formData.address || !formData.filing_status || !formData.employment_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach((key) => {
        if (key !== "honeypot") {
          submitData.append(key, formData[key]);
        }
      });
      
      // Add files
      files.forEach((file) => {
        submitData.append("documents", file);
      });

      await axios.post(`${API}/tax-intake`, submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      setIsSuccess(true);
      toast.success("Tax intake form submitted successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      const message = error.response?.data?.detail || "Failed to submit form. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      address: "",
      filing_status: "",
      has_dependents: false,
      dependents_count: 0,
      employment_type: "",
      is_business_owner: false,
      filed_last_year: false,
      notes: "",
      honeypot: "",
    });
    setFiles([]);
    setIsSuccess(false);
  };

  if (isSuccess) {
    return (
      <div data-testid="tax-intake-page" className="min-h-screen pt-20">
        <section className="py-20 md:py-32 bg-[#F9F9F7]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div data-testid="intake-success" className="bg-white rounded-2xl p-12 shadow-xl">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-[#0F392B] mb-4">
                Intake Form Submitted!
              </h2>
              <p className="font-body text-[#4A5568] mb-6">
                Thank you for submitting your tax client intake form. We've sent a
                confirmation email to <span className="font-semibold">{formData.email}</span>.
              </p>
              <p className="font-body text-[#4A5568] mb-8">
                Our team will review your information and contact you within 1-2
                business days to discuss your tax preparation needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={resetForm}
                  data-testid="submit-another-btn"
                  className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-8 py-3"
                >
                  Submit Another Form
                </Button>
                <a href="/booking">
                  <Button
                    variant="outline"
                    className="border-2 border-[#0F392B] text-[#0F392B] rounded-full px-8 py-3"
                  >
                    Book Appointment
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="tax-intake-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-[#0F392B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37] blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
              TAX CLIENT INTAKE
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
              New Client Information Form
            </h1>
            <p className="font-body text-white/80 max-w-xl mx-auto">
              Please complete this form to help us prepare your taxes accurately
              and efficiently.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Honeypot */}
            <input
              type="text"
              name="honeypot"
              value={formData.honeypot}
              onChange={handleInputChange}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Personal Information */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="font-heading text-xl font-semibold text-[#0F392B] mb-6 flex items-center gap-3">
                <User className="w-5 h-5 text-[#D4AF37]" />
                Personal Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label className="font-body text-sm text-[#4A5568] mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    required
                    data-testid="intake-name"
                    className="h-12 border-stone-200"
                  />
                </div>

                <div>
                  <Label className="font-body text-sm text-[#4A5568] mb-2 block">
                    Email Address *
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      required
                      data-testid="intake-email"
                      className="pl-10 h-12 border-stone-200"
                    />
                  </div>
                </div>

                <div>
                  <Label className="font-body text-sm text-[#4A5568] mb-2 block">
                    Phone Number *
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      required
                      data-testid="intake-phone"
                      className="pl-10 h-12 border-stone-200"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Label className="font-body text-sm text-[#4A5568] mb-2 block">
                    Address *
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-5 h-5 text-[#A0AEC0]" />
                    <Textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Main St, City, State, ZIP"
                      required
                      data-testid="intake-address"
                      className="pl-10 border-stone-200 min-h-[80px]"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Tax Information */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="font-heading text-xl font-semibold text-[#0F392B] mb-6 flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                Tax Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="font-body text-sm text-[#4A5568] mb-2 block">
                    Filing Status *
                  </Label>
                  <Select
                    value={formData.filing_status}
                    onValueChange={(value) => handleSelectChange("filing_status", value)}
                  >
                    <SelectTrigger data-testid="intake-filing-status" className="h-12">
                      <SelectValue placeholder="Select filing status" />
                    </SelectTrigger>
                    <SelectContent>
                      {filingStatuses.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="font-body text-sm text-[#4A5568] mb-2 block">
                    Employment Type *
                  </Label>
                  <Select
                    value={formData.employment_type}
                    onValueChange={(value) => handleSelectChange("employment_type", value)}
                  >
                    <SelectTrigger data-testid="intake-employment" className="h-12">
                      <SelectValue placeholder="Select employment type" />
                    </SelectTrigger>
                    <SelectContent>
                      {employmentTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 space-y-4">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="has_dependents"
                      checked={formData.has_dependents}
                      onCheckedChange={(checked) => handleCheckboxChange("has_dependents", checked)}
                      data-testid="intake-dependents"
                    />
                    <Label htmlFor="has_dependents" className="font-body text-sm text-[#4A5568]">
                      I have dependents
                    </Label>
                  </div>

                  {formData.has_dependents && (
                    <div className="ml-6">
                      <Label className="font-body text-sm text-[#4A5568] mb-2 block">
                        Number of Dependents
                      </Label>
                      <Input
                        name="dependents_count"
                        type="number"
                        min="0"
                        value={formData.dependents_count}
                        onChange={handleInputChange}
                        data-testid="intake-dependents-count"
                        className="w-32 h-10 border-stone-200"
                      />
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="is_business_owner"
                      checked={formData.is_business_owner}
                      onCheckedChange={(checked) => handleCheckboxChange("is_business_owner", checked)}
                      data-testid="intake-business-owner"
                    />
                    <Label htmlFor="is_business_owner" className="font-body text-sm text-[#4A5568]">
                      I am a business owner
                    </Label>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="filed_last_year"
                      checked={formData.filed_last_year}
                      onCheckedChange={(checked) => handleCheckboxChange("filed_last_year", checked)}
                      data-testid="intake-filed-last-year"
                    />
                    <Label htmlFor="filed_last_year" className="font-body text-sm text-[#4A5568]">
                      I filed taxes last year
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Document Upload */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="font-heading text-xl font-semibold text-[#0F392B] mb-6 flex items-center gap-3">
                <Upload className="w-5 h-5 text-[#D4AF37]" />
                Upload Documents
              </h2>
              <p className="font-body text-sm text-[#4A5568] mb-4">
                Upload your W-2s, 1099s, ID, and other relevant tax documents (Max 10MB per file)
              </p>

              <div className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center hover:border-[#D4AF37] transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  data-testid="intake-file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-12 h-12 text-[#A0AEC0] mb-4" />
                  <span className="font-body text-[#0F392B] font-medium">
                    Click to upload files
                  </span>
                  <span className="font-body text-sm text-[#A0AEC0] mt-1">
                    PDF, JPG, PNG, DOC (Max 10MB each)
                  </span>
                </label>
              </div>

              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-stone-50 rounded-lg p-3"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#0F392B]" />
                        <span className="font-body text-sm text-[#4A5568]">
                          {file.name}
                        </span>
                        <span className="font-body text-xs text-[#A0AEC0]">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Notes */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="font-heading text-xl font-semibold text-[#0F392B] mb-6 flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-[#D4AF37]" />
                Additional Information
              </h2>

              <div>
                <Label className="font-body text-sm text-[#4A5568] mb-2 block">
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional information we should know about your tax situation..."
                  rows={4}
                  data-testid="intake-notes"
                  className="border-stone-200"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="submit-intake-btn"
                className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-12 py-6 text-lg font-medium transition-all duration-300 disabled:opacity-50"
              >
                {isSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    Submit Intake Form
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              <p className="font-body text-xs text-[#A0AEC0] mt-4">
                Your information is secure and will only be used for tax preparation purposes.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
